<script>
import ajax from './ajax';
import UploadDragger from './upload-dragger.vue';

export default {
  inject: ['uploader'],
  components: {
    UploadDragger
  },
  props: {
    type: String,
    action: {
      type: String,
      required: true
    },
    name: {
      type: String,
      default: 'file'
    },
    data: Object,
    headers: Object,
    withCredentials: Boolean,
    multiple: Boolean,
    accept: String,
    onStart: Function,
    onProgress: Function,
    onSuccess: Function,
    onError: Function,
    beforeUpload: Function,
    drag: Boolean,
    onPreview: {
      type: Function,
      default: function() {}
    },
    onRemove: {
      type: Function,
      default: function() {}
    },
    fileList: Array,
    autoUpload: Boolean,
    listType: String,
    httpRequest: {
      type: Function,
      default: ajax
    },
    disabled: Boolean,
    limit: Number,
    onExceed: Function
  },

  data() {
    return {
      uploadFolder: false,
      mouseover: false,
      reqs: {}
    };
  },

  methods: {
    isImage(str) {
      return str.indexOf('image') !== -1;
    },
    handleChange(ev) {
      const files = ev.target.files;

      if (!files) return;
      this.uploadFiles(files);
    },

    uploadFiles(files, isDragged = false) {
      //console.log(`uploadFiles: files = ${JSON.stringify(files)}`);

      if (this.limit && this.fileList.length + files.length > this.limit) {
        this.onExceed && this.onExceed(files, this.fileList);
        return;
      }

      let postFiles = Array.prototype.slice.call(files);

      if (!isDragged && this.accept) {
        postFiles = postFiles.filter(file => {
          if (!file.webkitRelativePath) {
            //for normal <input> files, no more filtering required
            return true;
          }
          const { type, name } = file;
          const extension = name.indexOf('.') > -1
            ? `.${ name.split('.').pop().toLowerCase() }`
            : '';
          const baseType = type.replace(/\/.*$/, '');
          return this.accept.split(',')
            .map(type => type.trim())
            .filter(type => type)
            .some(acceptedType => {
              if (/\..+$/.test(acceptedType)) {
                return extension === acceptedType;
              }
              if (/\/\*$/.test(acceptedType)) {
                return baseType === acceptedType.replace(/\/\*$/, '');
              }
              if (/^[^\/]+\/[^\/]+$/.test(acceptedType)) {
                return type === acceptedType;
              }
              return false;
            });
        })
      }

      if (!this.multiple) { postFiles = postFiles.slice(0, 1); }

      if (postFiles.length === 0) { return; }

      postFiles.forEach(rawFile => {
        //NOTE: for rawFile gotten with webkitdirectory <input>, add "pathName" here; 
        //      set value as "" for normal <input>
        //      for rawFile gotten with dragged, the "pathName" has been set by <UploadDragger>
        if (!isDragged) {
          rawFile.pathName = rawFile.webkitRelativePath || ""
        }

        this.onStart(rawFile);
        if (this.autoUpload) this.upload(rawFile);
      });
    },

    upload(rawFile) {
      this.$refs.input.value = null;

      if (!this.beforeUpload) {
        return this.post(rawFile);
      }

      const before = this.beforeUpload(rawFile);
      if (before && before.then) {
        before.then(processedFile => {
          const fileType = Object.prototype.toString.call(processedFile);
          //console.log(`upload.before.then: name = ${rawFile.name} processedFile = ${JSON.stringify(processedFile)}`);

          if (fileType === '[object File]' || fileType === '[object Blob]') {
            if (fileType === '[object Blob]') {
              processedFile = new File([processedFile], rawFile.name, {
                type: rawFile.type
              });
            }
            for (const p in rawFile) {
              if (rawFile.hasOwnProperty(p)) {
                processedFile[p] = rawFile[p];
              }
            }
            this.post(processedFile);
          } else {
            //console.log(`upload.resolve: name = ${rawFile.name} processedFile = ${JSON.stringify(processedFile)}`);
            if (processedFile.skip) {
              this.onRemove(null, rawFile);
            }
            else if (processedFile.data) {
              this.post(rawFile, processedFile.data);
            }
            else {
              this.post(rawFile);
            }
          }
        }, () => {
          //console.log(`upload.before.reject: name = ${rawFile.name} called`);
          this.onRemove(null, rawFile);
        });
      } else if (before !== false) {
        //console.log(`upload.before.true: name = ${rawFile.name} called`);
        this.post(rawFile);
      } else {
        //console.log(`upload.before.false: name = ${rawFile.name} called`);
        this.onRemove(null, rawFile);
      }
    },
    abort(file) {
      const { reqs } = this;
      if (file) {
        let uid = file;
        if (file.uid) uid = file.uid;
        if (reqs[uid]) {
          reqs[uid].abort();
        }
      } else {
        Object.keys(reqs).forEach((uid) => {
          if (reqs[uid]) reqs[uid].abort();
          delete reqs[uid];
        });
      }
    },
    post(rawFile, embededData = null) {
      const { uid } = rawFile;
      let newData;
      if (embededData) {
        newData = {
          ...this.data,
          ...embededData,
        }
      }
      else {
        newData = this.data;
      }

      const options = {
        headers: this.headers,
        withCredentials: this.withCredentials,
        file: rawFile,
        data: newData,
        filename: this.name,
        action: this.action,
        onProgress: e => {
          this.onProgress(e, rawFile);
        },
        onSuccess: res => {
          this.onSuccess(res, rawFile);
          delete this.reqs[uid];
        },
        onError: err => {
          this.onError(err, rawFile);
          delete this.reqs[uid];
        }
      };
      const req = this.httpRequest(options);
      this.reqs[uid] = req;
      if (req && req.then) {
        req.then(options.onSuccess, options.onError);
      }
    },
    handleClick(selectFolder = false) {
      if (!this.disabled) {
        this.uploadFolder = selectFolder;
        this.$nextTick(() => {
          this.$refs.input.value = null;
          //NOTE: by applying nextTick(), we can avoid setting the value directly
          //this.$refs.input.webkitdirectory = selectFolder;
          this.$refs.input.click();
        })
      }
    },
    handleKeydown(e) {
      if (e.target !== e.currentTarget) return;
      if (e.keyCode === 13 || e.keyCode === 32) {
        this.handleClick();
      }
    }
  },

  render(h) {
    let {
      //handleClick,
      drag,
      name,
      handleChange,
      multiple,
      accept,
      listType,
      uploadFiles,
      disabled,
      //handleKeydown
    } = this;
    const data = {
      class: {
        'el-upload': true
      },
      on: {
        //NOTE: now input will be triggered by explicitly calling handleClick() instead
        //click: handleClick,
        //keydown: handleKeydown
      }
    };
    data.class[`el-upload--${listType}`] = true;
    return (
      <div {...data} tabindex="0" >
        {
          drag
            ? <upload-dragger disabled={disabled} on-file={uploadFiles}>{this.$slots.default}</upload-dragger>
            : this.$slots.default
        }
        <input class="el-upload__input" type="file" ref="input" name={name} on-change={handleChange} webkitdirectory={this.uploadFolder} multiple={multiple} accept={accept}></input>
      </div>
    );
  }
};
</script>
